import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import axios from "axios";
import { BRAND_COLORS } from "@/lib/menuData";

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback image in case pizza has no image
const DEFAULT_PIZZA_IMAGE = "https://customer-assets.emergentagent.com/job_f79dc26b-5e50-42ae-bcd5-79291907f1dd/artifacts/cxfzk88v_HBP_Buffalo_Chicken_Pizza_webready.jpg";

export default function PizzaPromoPopup({ show, onClose }) {
  const navigate = useNavigate();
  const [pizzas, setPizzas] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [pizzaStatus, setPizzaStatus] = useState(null); // { isOpen, message, nextOpen }

  useEffect(() => {
    if (show) {
      fetchSpecialtyPizzas();
      fetchPizzaStatus();
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [show]);

  const fetchSpecialtyPizzas = async () => {
    try {
      const res = await axios.get(`${API}/api/menu/specialty-pizzas`);
      if (res.data && res.data.length > 0) {
        setPizzas(res.data);
      }
    } catch (e) {
      console.error("Failed to fetch specialty pizzas:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPizzaStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/concepts/status`);
      setPizzaStatus(res.data?.pizza?.currentStatus || null);
    } catch (e) {
      /* fail-open: if we can't read status, allow the order */
    }
  };

  const handleOrderNow = () => {
    onClose();
    navigate("/order/pizza");
  };

  const nextPizza = () => {
    setCurrentIndex((prev) => (prev + 1) % pizzas.length);
  };

  const prevPizza = () => {
    setCurrentIndex((prev) => (prev - 1 + pizzas.length) % pizzas.length);
  };

  if (!show) return null;
  
  // If still loading or no pizzas available, don't show popup
  if (loading || pizzas.length === 0) {
    if (!loading && pizzas.length === 0) {
      // No available specialty pizzas, close popup
      setTimeout(onClose, 0);
    }
    return null;
  }

  const currentPizza = pizzas[currentIndex];

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
    >
      <div 
        className={`relative max-w-sm w-full transition-all duration-300 ${isVisible ? 'scale-100' : 'scale-95'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          data-testid="promo-close-btn"
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 active:scale-95"
          style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
        >
          <X className="h-5 w-5 text-white" />
        </button>

        {/* Pizza Card */}
        <div className="rounded-2xl overflow-hidden shadow-2xl bg-white">
          {/* Pizza Image */}
          <div className="relative h-56 overflow-hidden">
            <img
              src={currentPizza.image || DEFAULT_PIZZA_IMAGE}
              alt={currentPizza.name}
              className="w-full h-full object-cover"
              data-testid="promo-pizza-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            
            {/* Navigation arrows if multiple pizzas */}
            {pizzas.length > 1 && (
              <>
                <button 
                  onClick={(e) => { e.stopPropagation(); prevPizza(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); nextPizza(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}

            {/* Badge */}
            <div className="absolute top-4 left-4">
              <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                SPECIALTY PIZZA
              </span>
            </div>

            {/* Pizza name and price */}
            <div className="absolute bottom-4 left-4 right-4 text-white">
              <h3 className="text-2xl font-bold" style={{ fontFamily: "Montserrat" }}>
                {currentPizza.name}
              </h3>
              <p className="text-lg font-semibold mt-1">${currentPizza.price?.toFixed(2)} each</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <p className="text-gray-600 text-sm mb-4">
              Fresh from Hunt Brothers Pizza! Hand-tossed crust with premium toppings.
            </p>
            
            {/* Dots indicator */}
            {pizzas.length > 1 && (
              <div className="flex justify-center gap-2 mb-4">
                {pizzas.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentIndex ? "bg-green-600" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            )}

            {(() => {
              const isOpen = pizzaStatus ? pizzaStatus.isOpen !== false : true;
              if (!isOpen) {
                return (
                  <div
                    data-testid="promo-kitchen-closed"
                    className="w-full py-3 rounded-xl text-center"
                    style={{ backgroundColor: "#FEF2F2", border: "2px solid #FECACA" }}
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Clock className="h-4 w-4" style={{ color: "#B91C1C" }} />
                      <p className="font-bold text-sm" style={{ color: "#B91C1C" }}>
                        Sorry! Our kitchen is currently closed
                      </p>
                    </div>
                    {pizzaStatus?.nextOpen && (
                      <p className="text-xs" style={{ color: "#7F1D1D" }} data-testid="promo-next-open">
                        Opens {pizzaStatus.nextOpen}
                      </p>
                    )}
                  </div>
                );
              }
              return (
                <button
                  data-testid="promo-order-btn"
                  onClick={handleOrderNow}
                  className="w-full py-3 rounded-xl text-white font-bold text-lg transition-transform active:scale-[0.98]"
                  style={{ backgroundColor: BRAND_COLORS.deepTeal }}
                >
                  Order Pizza Now
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
